import { calculationLimits, crossingTypes, designFastVariables, designRoadClasses, designVehicles } from "store/calculation.config";
import { calculateAccelerationAlongSlope } from "utilities/genericRoadUtilities/calculateAccelerationAlongSlope";
import { calculateClothoideParameter } from "utilities/genericRoadUtilities/calculateClothoideParameter";
import { calculateSpeedFromRadius } from "utilities/genericRoadUtilities/calculateSpeedFromRadius";
import { calculateSuperelevationRunoffLength } from "utilities/genericRoadUtilities/calculateSuperelevationRunoffLength";

export const calculateAcceleration = (input) => {

    const errors = {
        TOOLS_SPEEDLIMIT_TOO_LOW: false,
        TOOLS_SPEEDLIMIT_TOO_HIGH: false,
        TOOLS_LONGITUDAL_SLOPE_TOO_LOW: false,
        TOOLS_LONGITUDAL_SLOPE_TOO_HIGH: false,
        TOOLS_HORIZONTAL_RADIUS_IS_EQUAL_ZERO: false,
        TOOLS_CROSS_SLOPE_TOO_LOW: false,
        TOOLS_CROSS_SLOPE_TOO_HIGH: false,
        TOOLS_RADIUS_DIRECTION_CORRECTED: false,
        TOOLS_RAMP_RADIUS_HIGHER_THAN_MAIN_ROAD_RADIUS: false,
        TOOLS_RAMP_RADIUS_LOWER_THAN_MAIN_ROAD_RADIUS: false,
        TOOLS_CROSS_SLOPE_DIRECTION_CORRECTED: false,
        TOOLS_RAMP_CONNECTION_CROSS_SLOPE_NOT_BETWEEN_RAMP_AND_MAIN_ROAD_CROSS_SLOPE: false,
        TOOLS_RAMP_CONNECTION_AND_MAIN_ROAD_CROSS_SLOPE_DIFFERENCE_TOO_HIGH: false,
        TOOLS_INTIIAL_SPEED_BELOW_REQUIRMENTS: false,
        TOOLS_INITIAL_SPEED_EQUAL_OR_ABOVE_SPEELIMIT: false,
        TOOLS_ACCELERATION_NOT_POSSIBLE_VEHICLE_NOT_ACCELERATING: false,
        TOOLS_ACCELERATION_NOT_POSSIBLE_SPEEDLIMIT_ABOVE_VEHICLE_MAX_SPEED: false,
        TOOLS_ACCELERATION_LENGTH_CORRECTED_TO_TIME_REQUIREMENT: false,
    }

    let calculationContext = {
        normalizedInput: {...input},
        wasNormalized: false,
        errorsToSet: [],
        errorsToUnset: [],
        input:
        {
            designClass: {value: null, valid: null, severity: null},
            crossingType: {value: null, valid: null, severity: null},
            speedlimit: {value: null, valid: null, severity: null},
            slope: {value: null, valid: null, severity: null},
            mainRoadRadius: {value: null, valid: null, severity: null},
            rampRadius: {value: null, valid: null, severity: null},
            rampCrossSlope: {value: null, valid: null, severity: null},
            rampConnectionCrossSlope: {value: null, valid: null, severity: null},
            mainRoadCrossSlope: {value: null, valid: null, severity: null},
            designVehicle: {value: null, valid: null, severity: null},
        },
        derived:
        {
            initialSpeed: {value: null, valid: null, severity: null},
            wheelbase: {value: designFastVariables.wheelbase, valid: null, severity: null},
            relativeVerticalSpeed: {value: designFastVariables.relativeVerticalSpeed, valid: null, severity: null},
        },
        output:
        {
            accelerationLength: {value: null, valid: null, severity: null},
            transitionLength: {value: null, valid: null, severity: null},
            curvedRunoffLength: {value: null, valid: null, severity: null},
            tangentialRunoffLength: {value: null, valid: null, severity: null},
            clothoideParameter: {value: null, valid: null, severity: null},
            accelerationData: {value: null, valid: null, severity: null},
        }
    }



    validateValues(calculationContext, errors);
    validateRelations(calculationContext, errors);
    calculateDerived(calculationContext, errors);
    calulcateOutput(calculationContext, errors);
    
    calculationContext.errorsToSet = Object.keys(errors).filter(key => errors[key]);
    calculationContext.errorsToUnset = Object.keys(errors).filter(key => !errors[key]);


    return (calculationContext);
}

const validateValues = (ctx, errors) => {

    // #region  design class
    if(ctx.normalizedInput.strictMode)
    {
        if(ctx.normalizedInput.designClass.trim() !== '')
        {
            let designClassObject = designRoadClasses[ctx.normalizedInput.designClass];
            if(designClassObject) ctx.input.designClass = {value: designClassObject, valid: true, severity: null};
            else ctx.input.designClass = {value: null, valid: false, severity: 'error'};
        }
    }
    // #endregion

    // #region  crossing type
    if(ctx.normalizedInput.crossingType.trim() !== '')
    {
        let crossingTypeObject = crossingTypes.acceleration[ctx.normalizedInput.crossingType];
        if(crossingTypeObject) ctx.input.crossingType = {value: ctx.normalizedInput.crossingType, valid: true, severity: null};
        else ctx.input.crossingType = {value: null, valid: false, severity: 'error'};
    }
    // #endregion

    // #region speedlimit
    if(ctx.normalizedInput.speedlimit.trim() !== '')
    {
        const speedlimit = Number(ctx.normalizedInput.speedlimit.trim());
        if(speedlimit < calculationLimits.minSpeed)
        {
            ctx.input.speedlimit = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_SPEEDLIMIT_TOO_LOW = true;
        }
        else if(speedlimit > calculationLimits.maxSpeed)
        {
            ctx.input.speedlimit = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_SPEEDLIMIT_TOO_HIGH = true;
        }
        else
        {
            ctx.input.speedlimit = {value: speedlimit, valid: true, severity: null};
        }
    }
    // #endregion

    // #region slope
    if(ctx.normalizedInput.slope.trim() !== '')
    {
        const slope = Number(ctx.normalizedInput.slope.trim());
        if(slope < calculationLimits.minLongitudalSlope)
        {
            ctx.input.slope = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_LONGITUDAL_SLOPE_TOO_LOW = true;
        }
        else if(slope > calculationLimits.maxLongitudalSlope)
        {
            ctx.input.slope = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_LONGITUDAL_SLOPE_TOO_HIGH = true;
        }
        else
        {
            ctx.input.slope = {value: slope, valid: true, severity: null};
        }
    }
    // #endregion

    // #region radius on main road
    if(ctx.normalizedInput.mainRoadRadius.trim() !== '')
    {
        const mainRoadRadius = Number(ctx.normalizedInput.mainRoadRadius.trim());

        if(mainRoadRadius === 0)
        {
            ctx.input.mainRoadRadius = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_HORIZONTAL_RADIUS_IS_EQUAL_ZERO = true;
        }
        else
        {
            ctx.input.mainRoadRadius = {value: mainRoadRadius, valid: true, severity: null};
        }
    }
    // #endregion

    // #region radius on ramp
    if(ctx.normalizedInput.rampRadius.trim() !== '')
    {
        const rampRadius = Number(ctx.normalizedInput.rampRadius.trim());

        if(rampRadius === 0)
        {
            ctx.input.rampRadius = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_HORIZONTAL_RADIUS_IS_EQUAL_ZERO = true;
        }
        else
        {
            ctx.input.rampRadius = {value: rampRadius, valid: true, severity: null};
        }
    }
    // #endregion

    // #region cross slope on ramp
    if(ctx.normalizedInput.rampCrossSlope.trim() !== '')
    {
        const rampCrossSlope = Number(ctx.normalizedInput.rampCrossSlope.trim());
        if(rampCrossSlope < calculationLimits.minCrossSlope)
        {
            ctx.input.rampCrossSlope = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_CROSS_SLOPE_TOO_LOW = true;
        }
        else if(rampCrossSlope > calculationLimits.maxCrossSlope)
        {
            ctx.input.rampCrossSlope = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_CROSS_SLOPE_TOO_HIGH = true;
        }
        else
        {
            ctx.input.rampCrossSlope = {value: rampCrossSlope, valid: true, severity: null};
        }
    }
    // #endregion

    // #region cross slope on ramp connection
    if(ctx.normalizedInput.rampConnectionCrossSlope.trim() !== '')
    {
        const rampConnectionCrossSlope = Number(ctx.normalizedInput.rampConnectionCrossSlope.trim());
        if(rampConnectionCrossSlope < calculationLimits.minCrossSlope)
        {
            ctx.input.rampConnectionCrossSlope = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_CROSS_SLOPE_TOO_LOW = true;
        }
        else if(rampConnectionCrossSlope > calculationLimits.maxCrossSlope)
        {
            ctx.input.rampConnectionCrossSlope = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_CROSS_SLOPE_TOO_HIGH = true;
        }
        else
        {
            ctx.input.rampConnectionCrossSlope = {value: rampConnectionCrossSlope, valid: true, severity: null};
        }
    }
    // #endregion

    // #region cross slope on main road
    if(ctx.normalizedInput.mainRoadCrossSlope.trim() !== '')
    {
        const mainRoadCrossSlope = Number(ctx.normalizedInput.mainRoadCrossSlope.trim());
        if(mainRoadCrossSlope < calculationLimits.minCrossSlope)
        {
            ctx.input.mainRoadCrossSlope = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_CROSS_SLOPE_TOO_LOW = true;
        }
        else if(mainRoadCrossSlope > calculationLimits.maxCrossSlope)
        {
            ctx.input.mainRoadCrossSlope = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_CROSS_SLOPE_TOO_HIGH = true;
        }
        else
        {
            ctx.input.mainRoadCrossSlope = {value: mainRoadCrossSlope, valid: true, severity: null};
        }
    }
    // #endregion

    // #region design vehicle
    if(ctx.normalizedInput.designVehicle.trim() !== '')
    {
        let designVehicleObject = designVehicles[ctx.normalizedInput.designVehicle];
        if(designVehicleObject) ctx.input.designVehicle = {value: designVehicleObject, valid: true, severity: null};
        else ctx.input.designVehicle = {value: null, valid: false, severity: 'error'};
    }
    // #endregion
}

const validateRelations = (ctx, errors) => {
    // #region validating curve combination and radius sign
    if(ctx.input.crossingType.valid)
    {
        if(ctx.input.mainRoadRadius.valid)
        {
            if(ctx.input.crossingType.value === 'right-right' && ctx.input.mainRoadRadius.value < 0)
            {
                ctx.normalizedInput.mainRoadRadius = Math.abs(ctx.input.mainRoadRadius.value);
                ctx.wasNormalized = true;
                ctx.input.mainRoadRadius = {
                    value: Math.abs(ctx.input.mainRoadRadius.value),
                    valid: true,
                    severity: null
                }
                errors.TOOLS_RADIUS_DIRECTION_CORRECTED = true;
            }
            else if((ctx.input.crossingType.value === 'right-left'
                || ctx.input.crossingType.value === 'straight-left'
                || ctx.input.crossingType.value === 'left-left')
                && ctx.input.mainRoadRadius.value > 0)
            {
                ctx.normalizedInput.mainRoadRadius = -Math.abs(ctx.input.mainRoadRadius.value);
                ctx.wasNormalized = true;
                ctx.input.mainRoadRadius = {
                    value: -Math.abs(ctx.input.mainRoadRadius.value),
                    valid: true,
                    severity: null
                }
                errors.TOOLS_RADIUS_DIRECTION_CORRECTED = true;
            }
        }

        if(ctx.input.rampRadius.valid)
        {
            if(ctx.input.crossingType.value === 'left-left' && ctx.input.rampRadius.value > 0)
            {
                ctx.normalizedInput.rampRadius = -Math.abs(ctx.input.rampRadius.value);
                ctx.wasNormalized = true;
                ctx.input.rampRadius = {
                    value: Math.abs(ctx.input.rampRadius.value),
                    valid: true,
                    severity: null
                }
                errors.TOOLS_RADIUS_DIRECTION_CORRECTED = true;
            }
            else if((ctx.input.crossingType.value === 'right-right'
                || ctx.input.crossingType.value === 'right-left'
                || ctx.input.crossingType.value === 'right-stright')
                && ctx.input.rampRadius.value < 0)
            {
                ctx.normalizedInput.rampRadius = Math.abs(ctx.input.rampRadius.value);
                ctx.wasNormalized = true;
                ctx.input.rampRadius = {
                    value: Math.abs(ctx.input.rampRadius.value),
                    valid: true,
                    severity: null
                }
                errors.TOOLS_RADIUS_DIRECTION_CORRECTED = true;
            }
        }

        if(ctx.input.mainRoadRadius.valid && ctx.input.rampRadius.valid)
        {
            if(ctx.input.crossingType.value === 'right-right' && ctx.input.rampRadius.value >= ctx.input.mainRoadRadius.value)
            {
                ctx.input.rampRadius = {
                    value: ctx.input.rampRadius.value,
                    valid: false,
                    severity: 'error'
                };

                ctx.input.mainRoadRadius = {
                    value: ctx.input.mainRoadRadius.value,
                    valid: false,
                    severity: 'error'
                };
                errors.TOOLS_RAMP_RADIUS_HIGHER_THAN_MAIN_ROAD_RADIUS = true;
            }
            else if(ctx.input.crossingType.value === 'left-left' && ctx.input.rampRadius.value >= ctx.input.mainRoadRadius.value)
            {
                ctx.input.rampRadius = {
                    value: ctx.input.rampRadius.value,
                    valid: false,
                    severity: 'error'
                };

                ctx.input.mainRoadRadius = {
                    value: ctx.input.mainRoadRadius.value,
                    valid: false,
                    severity: 'error'
                };
                errors.TOOLS_RAMP_RADIUS_LOWER_THAN_MAIN_ROAD_RADIUS = true;
            }
        }
    }
    // #endregion
    
    // #region validating curve combination and cross slope
    if(ctx.input.crossingType.valid && ctx.input.rampCrossSlope)
    {
        if(ctx.input.crossingType.value === 'left-left' && ctx.input.rampCrossSlope.value < 0)
        {
            ctx.normalizedInput.rampCrossSlope = Math.abs(ctx.input.rampCrossSlope.value);
            ctx.wasNormalized = true;
            ctx.input.rampCrossSlope = {
                value: Math.abs(ctx.input.rampCrossSlope.value),
                valid: true,
                severity: null
            }
            errors.TOOLS_CROSS_SLOPE_DIRECTION_CORRECTED = true;
        }
        else if((ctx.input.crossingType.value === 'right-right'
            || ctx.input.crossingType.value === 'right-left'
            || ctx.input.crossingType.value === 'right-straight')
            && ctx.input.rampCrossSlope.value > 0
        )
        {
            ctx.normalizedInput.rampCrossSlope = -Math.abs(ctx.input.rampCrossSlope.value);
            ctx.wasNormalized = true;
            ctx.input.rampCrossSlope = {
                value: -Math.abs(ctx.input.rampCrossSlope.value),
                valid: true,
                severity: null
            }
            errors.TOOLS_CROSS_SLOPE_DIRECTION_CORRECTED = true;
        }
    }

    if(ctx.input.crossingType.valid && ctx.input.mainRoadCrossSlope)
    {
        if(ctx.input.crossingType.value === 'right-right' && ctx.input.mainRoadCrossSlope.value > 0)
        {
            ctx.normalizedInput.mainRoadCrossSlope = -Math.abs(ctx.input.mainRoadCrossSlope.value);
            ctx.wasNormalized = true;
            ctx.input.mainRoadCrossSlope = {
                value: -Math.abs(ctx.input.mainRoadCrossSlope.value),
                valid: true,
                severity: null
            }
            errors.TOOLS_CROSS_SLOPE_DIRECTION_CORRECTED = true;
        }
        else if((ctx.input.crossingType.value === 'right-left'
            || ctx.input.crossingType.value === 'straight-left'
            || ctx.input.crossingType.value === 'left-left')
            && ctx.input.mainRoadCrossSlope.value < 0
        )
        {
            ctx.normalizedInput.mainRoadCrossSlope = Math.abs(ctx.input.mainRoadCrossSlope.value);
            ctx.wasNormalized = true;
            ctx.input.mainRoadCrossSlope = {
                value: -Math.abs(ctx.input.mainRoadCrossSlope.value),
                valid: true,
                severity: null
            }
            errors.TOOLS_CROSS_SLOPE_DIRECTION_CORRECTED = true;
        }
    }
    // #endregion

    // #region validating cross slopes relation
    if(ctx.input.mainRoadCrossSlope.valid && ctx.input.rampConnectionCrossSlope.valid && ctx.input.rampCrossSlope.valid)
    {
        if(ctx.input.rampConnectionCrossSlope.value < Math.min(ctx.input.mainRoadCrossSlope.value, ctx.input.rampCrossSlope.value)
            || ctx.input.rampConnectionCrossSlope.value > Math.max(ctx.input.mainRoadCrossSlope.value, ctx.input.rampCrossSlope.value))
        {
            errors.TOOLS_RAMP_CONNECTION_CROSS_SLOPE_NOT_BETWEEN_RAMP_AND_MAIN_ROAD_CROSS_SLOPE = true;
            
            ctx.input.rampCrossSlope.severity = 'warning';
            ctx.input.rampConnectionCrossSlope.severity = 'warning';
            ctx.input.mainRoadCrossSlope.severity = 'warning';

        }
    }

    if(ctx.input.mainRoadCrossSlope.valid && ctx.input.rampConnectionCrossSlope.valid)
    {
        if(Math.abs(ctx.input.mainRoadCrossSlope.value - ctx.input.rampConnectionCrossSlope.value) > 0.05)
        {
            errors.TOOLS_RAMP_CONNECTION_AND_MAIN_ROAD_CROSS_SLOPE_DIFFERENCE_TOO_HIGH = true;
            ctx.input.rampConnectionCrossSlope.severity = 'warning';
            ctx.input.mainRoadCrossSlope.severity = 'warning';
        }
    }
    // #endregion
}

const calculateDerived = (ctx, errors) => {
    let initialSpeed = null;

    if(ctx.input.crossingType.valid && ctx.input.crossingType.value === 'straight-left') initialSpeed = 80;
    else if(ctx.input.rampRadius.valid) initialSpeed = calculateSpeedFromRadius(ctx.input.rampRadius.value);

    if(initialSpeed)
    {
        ctx.derived.initialSpeed = {
            value: initialSpeed,
            valid: true,
            severity: null
        }

        if(ctx.normalizedInput.strictMode)
        {
            if(ctx.input.designClass.valid)
            {
                if(ctx.input.designClass.value.minInitialSpeed && initialSpeed < ctx.input.designClass.value.minInitialSpeed)
                {
                    ctx.input.rampRadius.severity = 'warning';
                    errors.TOOLS_INTIIAL_SPEED_BELOW_REQUIRMENTS = true;
                }
            }
        }
    }

    if(ctx.input.speedlimit.valid)
    {
        if(initialSpeed >= ctx.input.speedlimit.value)
        {
            errors.TOOLS_INITIAL_SPEED_EQUAL_OR_ABOVE_SPEELIMIT = true;
            ctx.input.speedlimit = {
                value: null,
                valid: false,
                severity: 'error'
            }
        }
    }  
}

const calulcateOutput = (ctx, errors) => {

    // #region acceleration length
    if(ctx.input.speedlimit.valid && ctx.input.designVehicle.valid && ctx.input.slope.valid && ctx.derived.initialSpeed.valid)
    {
        const {accelerationData, accelerationErrors} = calculateAccelerationAlongSlope({
            initialSpeed: ctx.derived.initialSpeed.value,
            slope: ctx.input.slope.value,
            designVehicle: ctx.input.designVehicle.value,
            speedlimit: ctx.input.speedlimit.value
        });
        
        if(accelerationErrors.length > 0)
        {
            ctx.output.accelerationLength = {
                value: null,
                valid: false,
                severity: 'error'
            }

            if(accelerationErrors.includes("TOOLS_ACCELERATION_NOT_POSSIBLE_VEHICLE_NOT_ACCELERATING")) errors.TOOLS_ACCELERATION_NOT_POSSIBLE_VEHICLE_NOT_ACCELERATING = true;
        }
        else
        {            
            const accelerationEnd = accelerationData.find(el => el.speed >= ctx.input.speedlimit.value);
            
            if(!accelerationEnd)
            {
                errors.TOOLS_ACCELERATION_NOT_POSSIBLE_SPEEDLIMIT_ABOVE_VEHICLE_MAX_SPEED = true;
                ctx.input.speedlimit = {
                    value: ctx.input.speedlimit,
                    valid: false,
                    severity: 'error'
                }
            }
            else
            {
                if(accelerationEnd.time < 6)
                {
                    errors.TOOLS_ACCELERATION_LENGTH_CORRECTED_TO_TIME_REQUIREMENT = true;
                    ctx.output.accelerationLength = {
                        value: accelerationEnd.distance +accelerationEnd.speed/3.6 * (3 - 0.5 * accelerationEnd.time),
                        valid: true,
                        severity: null
                    }
                }
                else
                {
                    ctx.output.accelerationLength = {
                        value: accelerationEnd.distance,
                        valid: true,
                        severity: null
                    }
                }
            }

            if(accelerationEnd)
            {
                accelerationData.forEach(el => {
                    if(el.speed < ctx.input.speedlimit.value) el.segment = 1;
                    else if(el.distance < ctx.output.accelerationLength.value) el.segment = 2;
                    else el.segment = 3;
                });


            }
            else
            {
                accelerationData.forEach(el => {
                    if(el.speed < ctx.input.speedlimit.value) el.segment = 1;
                });
            }

            
            
            if(accelerationData.length > 0)
            {
                ctx.output.accelerationData = {
                    value: accelerationData,
                    valid: true,
                    severity: null
                }
            }
        }

    }
    // #endregion

    // #region transition length
    if(ctx.input.speedlimit.valid)
    {
        if(ctx.input.speedlimit.value <= 60)
        {
            ctx.output.transitionLength = {
                value: 20,
                valid: true,
                severity: null
            }
        }
        else if(ctx.input.speedlimit.value <= 80)
        {
            ctx.output.transitionLength = {
                value: 30,
                valid: true,
                severity: null
            }
        }
        else if(ctx.input.speedlimit.value <= 90)
        {
            ctx.output.transitionLength = {
                value: 40,
                valid: true,
                severity: null
            }
        }
        else if(ctx.input.speedlimit.value > 90)
        {
            ctx.output.transitionLength = {
                value: 60,
                valid: true,
                severity: null
            }
        }
    }
    // #endregion

    // #region curved runoff length
    if(ctx.derived.initialSpeed.valid && ctx.input.rampCrossSlope.valid && ctx.input.rampConnectionCrossSlope.valid)
    {
        ctx.output.curvedRunoffLength = {
            value: calculateSuperelevationRunoffLength(
                ctx.derived.initialSpeed.value,
                Math.abs(ctx.input.rampCrossSlope.value - ctx.input.rampConnectionCrossSlope.value),
                ctx.derived.wheelbase.value,
                ctx.derived.relativeVerticalSpeed.value
            ),
            valid: true,
            severity: null
        }
    }
    // #endregion

    // #region tangential runoff length
    if(ctx.derived.initialSpeed.valid && ctx.input.mainRoadCrossSlope.valid && ctx.input.rampConnectionCrossSlope.valid)
    {
        ctx.output.tangentialRunoffLength = {
            value: calculateSuperelevationRunoffLength(
                ctx.derived.initialSpeed.value,
                Math.abs(ctx.input.mainRoadCrossSlope.value - ctx.input.rampConnectionCrossSlope.value),
                ctx.derived.wheelbase.value,
                ctx.derived.relativeVerticalSpeed.value
            ),
            valid: true,
            severity: null
        }
    }
    // #endregion

    // #region clothoide parameter
    if(ctx.input.crossingType.valid)
    {
        if(ctx.input.crossingType.value === 'right-straight' && ctx.input.rampRadius.valid && ctx.output.curvedRunoffLength.valid)
        {
            ctx.output.clothoideParameter = {
                value: calculateClothoideParameter({
                    firstRadius: ctx.input.rampRadius.value,
                    superelevationRunoffLength: ctx.output.curvedRunoffLength.value
                }),
                valid: true,
                severity: null
            }
        }
        else if(ctx.input.crossingType.value === 'right-right' && ctx.input.rampRadius.valid && ctx.input.mainRoadRadius.valid && ctx.output.curvedRunoffLength.valid)
        {
            ctx.output.clothoideParameter = {
                value: calculateClothoideParameter({
                    firstRadius: ctx.input.rampRadius.value,
                    secondRadius: ctx.input.mainRoadRadius.value,
                    superelevationRunoffLength: ctx.output.curvedRunoffLength.value
                }),
                valid: true,
                severity: null
            }
        }
        else if(ctx.input.crossingType.value === 'right-left' && ctx.input.rampRadius.valid && ctx.input.mainRoadRadius.valid && ctx.output.curvedRunoffLength.valid)
        {
            ctx.output.clothoideParameter = {
                value: calculateClothoideParameter({
                    firstRadius: ctx.input.mainRoadRadius.value,
                    secondRadius: ctx.input.rampRadius.value,
                    superelevationRunoffLength: ctx.output.curvedRunoffLength.value
                }),
                valid: true,
                severity: null
            }
        }
        else if(ctx.input.crossingType.value === 'straight-left'  && ctx.input.mainRoadRadius.valid && ctx.output.curvedRunoffLength.valid)
        {
            ctx.output.clothoideParameter = {
                value: calculateClothoideParameter({
                    firstRadius: ctx.input.mainRoadRadius.value,
                    superelevationRunoffLength: ctx.output.curvedRunoffLength.value
                }),
                valid: true,
                severity: null
            }
        }
        else if(ctx.input.crossingType.value === 'left-left' && ctx.input.rampRadius.valid && ctx.input.mainRoadRadius.valid && ctx.output.curvedRunoffLength.valid)
        {
            ctx.output.clothoideParameter = {
                value: calculateClothoideParameter({
                    firstRadius: ctx.input.mainRoadRadius.value,
                    secondRadius: ctx.input.rampRadius.value,
                    superelevationRunoffLength: ctx.output.curvedRunoffLength.value
                }),
                valid: true,
                severity: null
            }
        }
    }
    // #endregion
}